<template>
  <ContentWrap title="播报配置">
    <el-form :model="config" inline>
      <el-form-item label="功能状态">
        <el-switch v-model="config.enabled" />
      </el-form-item>
      <el-form-item label="每日额度">
        <el-input-number v-model="config.dailyQuota" :min="0" />
      </el-form-item>
      <el-form-item label="打扰率阈值(%)">
        <el-input-number v-model="config.disturbThreshold" :min="0" :max="100" :precision="2" />
      </el-form-item>
      <el-form-item>
        <el-button
          v-hasPermi="['bosscircle:scene:update']"
          type="primary"
          :loading="saving"
          @click="saveConfig"
        >
          保存
        </el-button>
      </el-form-item>
    </el-form>
    <el-form inline>
      <el-form-item label="省份"
        ><el-input v-model="weather.provinceName" placeholder="例如：江苏"
      /></el-form-item>
      <el-form-item label="城市"
        ><el-input v-model="weather.cityName" placeholder="例如：南京"
      /></el-form-item>
      <el-button :loading="weatherLoading" @click="previewWeather">天气预览</el-button>
      <span v-if="weatherResult" class="weather-result">
        {{ weatherResult.cityName }}：{{ weatherResult.weatherText }}，{{
          weatherResult.temperature
        }}℃
      </span>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-form :model="query" inline>
      <el-form-item label="用户昵称"
        ><BusinessLookupSelect v-model="query.userId" type="users" placeholder="请选择用户"
      /></el-form-item>
      <el-form-item label="场景">
        <el-select v-model="query.sceneType" clearable class="!w-140px">
          <el-option
            v-for="item in sceneOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="query.status" clearable class="!w-140px">
          <el-option
            v-for="(label, value) in statusLabels"
            :key="value"
            :label="label"
            :value="Number(value)"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="日期"
        ><el-date-picker v-model="query.sceneDate" value-format="YYYY-MM-DD"
      /></el-form-item>
    <el-form-item>
      <el-button type="primary" @click="search">查询</el-button>
    </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list">
      <el-table-column prop="id" label="编号" width="90" />
      <el-table-column label="用户昵称" min-width="120"><template #default="scope"><BusinessLookupName type="users" :id="scope.row.userId" /></template></el-table-column>
      <el-table-column prop="sceneDate" label="日期" width="120" />
      <el-table-column prop="sceneType" label="场景" width="110" />
      <el-table-column
        prop="weatherSummary"
        label="天气摘要"
        min-width="160"
        show-overflow-tooltip
      />
      <el-table-column
        prop="generatedText"
        label="播报内容"
        min-width="260"
        show-overflow-tooltip
      />
      <el-table-column label="状态" width="100">
        <template #default="scope">{{ statusLabels[scope.row.status] || '未知' }}</template>
      </el-table-column>
      <el-table-column prop="createTime" label="生成时间" width="180" />
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="load"
    />
  </ContentWrap>
</template>

<script setup lang="ts">
import * as SceneApi from '@/api/business/scene'

defineOptions({ name: 'BossCircleScene' })
const message = useMessage()
const loading = ref(false)
const saving = ref(false)
const weatherLoading = ref(false)
const list = ref<any[]>([])
const total = ref(0)
const weatherResult = ref<any>()
const statusLabels: Record<number, string> = {
  0: '生成中',
  1: '已展示',
  2: '喜欢',
  3: '忽略',
  4: '举报',
  5: '失败'
}
const sceneOptions = [
  { label: '晴天', value: 'SUNNY' },
  { label: '雨天', value: 'RAIN' },
  { label: '拜访', value: 'VISIT' },
  { label: '晚间', value: 'EVENING' },
  { label: '积极反馈', value: 'POSITIVE' }
]
const query = reactive({
  pageNo: 1,
  pageSize: 10,
  userId: undefined,
  sceneType: '',
  status: undefined,
  sceneDate: ''
})
const config = reactive<SceneApi.GreetSceneConfig>({
  enabled: false,
  dailyQuota: 0,
  disturbThreshold: 8,
  version: 0
})
const weather = reactive({ provinceName: '', cityName: '' })

const load = async () => {
  loading.value = true
  try {
    const data = await SceneApi.getGreetScenePage(query)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}
const loadConfig = async () => Object.assign(config, await SceneApi.getGreetSceneConfig())
const search = () => {
  query.pageNo = 1
  load()
}
const saveConfig = async () => {
  saving.value = true
  try {
    await SceneApi.saveGreetSceneConfig(config)
    message.success('保存成功')
    await loadConfig()
  } finally {
    saving.value = false
  }
}
const previewWeather = async () => {
  if (!weather.provinceName.trim() || !weather.cityName.trim())
    return message.warning('请输入省份和城市')
  weatherLoading.value = true
  try {
    weatherResult.value = await SceneApi.getWeatherPreview(
      weather.provinceName.trim(),
      weather.cityName.trim()
    )
  } finally {
    weatherLoading.value = false
  }
}
onMounted(() => {
  load()
  loadConfig()
})
</script>

<style scoped>
.weather-result {
  margin-left: 16px;
  color: var(--el-color-primary);
}
</style>
